<script setup lang="ts">
import { api } from '@/api';
import type { TrackItMeasurementInfoDto } from '@/api/generator';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { fullName } from '@/utils/helper';
import type { I18nKey } from '@/utils/typings';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { useTranslation } from 'i18next-vue';
import { useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import ImportedMeasurementOverviewFilter from './components/ImportedMeasurementOverviewFilter.vue';
import { useTrackItMeasurements } from './useTrackItMeasurements';

const { t } = useTranslation();
const router = useRouter();
const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);

function navigateToDetails(measurement: TrackItMeasurementInfoDto, hash?: string) {
  router.push({
    name: routenames.importedMeasurements.details,
    hash,
    params: { id: measurement.id },
  });
}

function importUserName(measurement: TrackItMeasurementInfoDto) {
  return fullName(measurement.importUser ?? undefined);
}

const headers = [
  {
    title: t('MeasurementDate'),
    key: 'measurementDate',
  },
  {
    title: t('TrackItAlias'),
    key: 'radiationUnit',
  },
  {
    title: t('TrackItMeasuringDevice'),
    key: 'measurementDevice',
  },
  {
    title: t('TrackItMeasuringSoftware'),
    key: 'measurementSoftware',
  },
  {
    title: t('ImportDate'),
    key: 'importDate',
  },
  {
    title: t('ImportUser' as I18nKey),
    key: 'importUser',
  },
] as const;

const {
  defaultOptions,
  filter,
  measurements,
  itemsLength,
  loading,
  loadingFirstTime,
  updateOptions,
} = useTrackItMeasurements(
  api.trackItImportApi.getTrackItMeasurementsForOverview.bind(api.trackItImportApi),
);
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('ImportedMeasurements' as I18nKey)" />
    </template>

    <ImportedMeasurementOverviewFilter v-model="filter" />
    <OverviewTable
      ref="table"
      class="mt-2"
      :headers="headers"
      :items="measurements"
      :itemsPerPage="defaultOptions.itemsPerPage"
      :serverSidePagination="true"
      :itemsLength="itemsLength"
      :hideDefaultFooter="false"
      :defaultSortBy="defaultOptions.sortBy"
      :loading="loading"
      itemLabel="Measurement"
      data-testid="DataTable_ImportedMeasurement"
      :height="height"
      fixedHeader
      @update:options="updateOptions"
      @click:row="
        (_event: unknown, { item }: { item: TrackItMeasurementInfoDto }) => navigateToDetails(item)
      "
    >
      <template #item.measurementDate="{ item }">
        <DateTimeFormatter :value="item.measurementDate" />
        <CommentCount
          :value="item"
          @click.stop="navigateToDetails(item, `#${TAB_PREFIX}comments`)"
        />
      </template>

      <template #item.importDate="{ item }">
        <DateTimeFormatter :value="item.importDate" />
      </template>

      <template #item.importUser="{ item }">
        {{ importUserName(item) ?? EMPTY_FIELD_SIGN }}
      </template>

      <template #item.measurementDevice="{ item }">
        {{ item.measurementDevice || EMPTY_FIELD_SIGN }}
      </template>

      <template #item.measurementSoftware="{ item }">
        {{ item.measurementSoftware || EMPTY_FIELD_SIGN }}
      </template>

      <template #item.radiationUnit="{ item }">
        {{ item.radiationUnit || EMPTY_FIELD_SIGN }}
      </template>
    </OverviewTable>
  </BasePage>
</template>
